import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';

/**
 * Load dashboard stats for the instructor shell.
 * Lightweight count queries — no full document loading.
 *
 * @param {string|null} instructorId
 * @param {string|null} orgId
 * @returns {{ videoQueueCount, completedReviewCount, lessonsThisWeek, clientCount, reportsToSend, loading }}
 */
export function useInstructorDashboardStats(instructorId, orgId) {
  const [stats, setStats] = useState({
    videoQueueCount: 0,
    completedReviewCount: 0,
    lessonsThisWeek: 0,
    clientCount: 0,
    reportsToSend: 0,
    loading: true,
  });

  useEffect(() => {
    if (!instructorId) return;
    let cancelled = false;

    async function load() {
      try {
        const results = await Promise.all([
          // Videos assigned to instructor, still in review
          countDocs('videos', [
            where('assignedTo', '==', instructorId),
            where('status', '==', 'in_review'),
          ]),
          // Completed reviews by instructor
          countDocs('videos', [
            where('assignedTo', '==', instructorId),
            where('status', '==', 'reviewed'),
          ]),
          // Lessons this week (created in last 7 days)
          countDocsRecentDays('lessonInstances', 'instructorId', instructorId, 7),
          // Client count in org
          orgId ? countDocs(PLATFORM_COLLECTIONS.CLIENT_PROFILES, [
            where('organizationId', '==', orgId),
          ]) : Promise.resolve(0),
          // Quick captures needing reports (complete but no report sent)
          orgId ? countDocs(`orgs/${orgId}/quickCaptures`, [
            where('status', '==', 'complete'),
          ]) : Promise.resolve(0),
        ]);

        if (cancelled) return;

        setStats({
          videoQueueCount: results[0],
          completedReviewCount: results[1],
          lessonsThisWeek: results[2],
          clientCount: results[3],
          reportsToSend: Math.min(results[4], 10), // cap display
          loading: false,
        });
      } catch (err) {
        console.warn('Dashboard stats load failed:', err.message);
        if (!cancelled) setStats(s => ({ ...s, loading: false }));
      }
    }

    load();
    return () => { cancelled = true; };
  }, [instructorId, orgId]);

  return stats;
}

async function countDocs(collectionPath, whereClauses = []) {
  try {
    const ref = collection(db, collectionPath);
    const q = query(ref, ...whereClauses);
    const snap = await getDocs(q);
    return snap.size;
  } catch (_) {
    return 0;
  }
}

async function countDocsRecentDays(collectionName, fieldName, fieldValue, days) {
  try {
    const ref = collection(db, collectionName);
    const q = query(ref, where(fieldName, '==', fieldValue));
    const snap = await getDocs(q);
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return snap.docs.filter(d => {
      const ts = d.data().createdAt;
      if (!ts) return false;
      const ms = ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
      return ms > cutoff;
    }).length;
  } catch (_) {
    return 0;
  }
}
